import React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { styled } from '@mui/system';
import moment from 'moment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MultipleIcon from '../../NRIcons/MultipleIcon';
import NRRelativeTime from '../NRRelativeTime/NRRelativeTime';
import NRSelect from '../NRControlledSelect/NRSelect';
import NRActivityFeedItemHeader from './NRActivityFeedItemHeader';
import NRActivityFeedIcon from './NRActivityFeedIcon';
import NRExportToCsvDialog from '../NRExportToCsvDialog/NRExportToCsvDialog';
import NRExportIcon from '../../NRIcons/NRExportIcon';
import { useTheme } from '@mui/material/styles';
import NRActivityFeedVirtualizedList from '../NRVirtualizedLists/NRActivityFeedVirtualizedList';
import NRCursorPagination from '../NRCursorPagination/NRCursorPagination';
import { useFlag } from '@openfeature/react-sdk';
import { FEATURE_FLAGS } from '../../constants/constants';
import { DEFAULT_PAGE_SIZE } from './NRActivityFeedContainer';

let ActivityEntityOptions = [
  { value: 'ALL', label: 'All' },
  { value: 'ASSET', label: 'Asset' },
  { value: 'ORGANIZATION', label: 'Organization' },
  { value: 'LICENSE_ISSUE', label: 'License Issues' },
  { value: 'MISCONFIGURATION', label: 'Misconfigurations' },
  { value: 'IDENTIFICATION', label: 'SBOM' },
  { value: 'SECRET', label: 'Secrets' },
  { value: 'VULNERABILITY', label: 'Vulnerabilities' }
];

const ACTIVITY_OPTIONS_BY_ENTITY = {
  VULNERABILITY: [
    { value: 'VULNERABILITY_ADDED', label: 'Added' },
    { value: 'VULNERABILITY_REMOVED', label: 'Removed' },
    { value: 'VULNERABILITY_UPDATED', label: 'Updated' },
    { value: 'VULNERABILITY_REMEDIATED', label: 'Remediated' }
  ],
  IDENTIFICATION: [
    { value: 'IDENTIFICATION_ADDED', label: 'Added' },
    { value: 'IDENTIFICATION_REMOVED', label: 'Removed' }
  ],
  ASSET: [
    { value: 'ASSET_CREATED', label: 'Created' },
    { value: 'ASSET_UPDATED', label: 'Updated' },
    { value: 'ASSET_ANALYSIS_COMPLETED', label: 'Completed' },
    { value: 'ASSET_RISK_CHANGED', label: 'Risk changed' }
  ],
  MISCONFIGURATION: [{ value: 'MISCONFIG_CHANGED', label: 'Changed' }],
  SECRET: [{ value: 'SECRET_REMEDIATED', label: 'Remediated' }],
  LICENSE_ISSUE: [{ value: 'LICENSE_ISSUE_REMEDIATED', label: 'Remediated' }],
  ORGANIZATION: [
    { value: 'GROUP_CREATED', label: 'Created' },
    { value: 'GROUP_UPDATED', label: 'Updated' },
    { value: 'GROUP_DELETED', label: 'Deleted' },
    { value: 'GROUP_MEMBERSHIP_CHANGED', label: 'Membership changed' },
    { value: 'ORG_SETTINGS_UPDATED', label: 'Settings updated' }
  ]
};

const AltAvatar = styled(props => <Avatar variant="circular" {...props} />)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.iconInfo,
  width: theme.spacing(4.5),
  height: theme.spacing(4.5)
}));

const ActivityFeedQuickSearch = React.memo(function ActivityFeedQuickSearch({
  searchInput,
  setSearchInput,
  selectedEntity,
  setSelectedEntity,
  selectedActivities,
  setSelectedActivities,
  activityOptions,
  isLoading,
  setExportToCsvDialogIsOpen
}) {
  const handleOnKeyDown = event => {
    if (event.keyCode === 13 && !event.shiftKey && searchInput?.length >= 3) {
      event.preventDefault();
    }
  };

  const { value: isSecretsEnable } = useFlag(FEATURE_FLAGS.SECRETS_DETECTION, false);
  if (!isSecretsEnable) {
    ActivityEntityOptions = ActivityEntityOptions.filter(option => option.value !== 'SECRETS');
  }

  const isActivitySelectDisabled = isLoading || selectedEntity === 'ALL' || activityOptions.length === 0;

  return (
    <Box sx={{ padding: 1, display: 'flex', flexGrow: 1, alignItems: 'center', gap: 2, minWidth: 0 }}>
      <TextField
        onKeyDown={handleOnKeyDown}
        multiline={false}
        sx={{
          flex: 1,
          minWidth: 0
        }}
        id="activity-feed-filters"
        placeholder={'Quick Search'}
        color={'secondary'}
        value={searchInput}
        onChange={e => setSearchInput(e.target.value)}
        label={
          <Typography display={'flex'}>
            <SearchIcon />
            Quick Search
          </Typography>
        }
        InputProps={
          !!searchInput
            ? {
                endAdornment: (
                  <Tooltip title="Clear">
                    <IconButton size="small" aria-label="delete" onClick={() => setSearchInput('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )
              }
            : null
        }
        variant="outlined"
        size="small"
      />
      <NRSelect
        sxProps={{
          width: 'fit-content',
          minWidth: 96,
          maxWidth: 220,
          flexShrink: 0,
          '& .MuiSelect-select': {
            whiteSpace: 'nowrap'
          }
        }}
        size={'small'}
        variant={'outlined'}
        id={`activity-entity-selector`}
        value={selectedEntity}
        showEmptyValue={false}
        onChange={setSelectedEntity}
        options={ActivityEntityOptions}
        disabled={isLoading}
        label={'View'}
      />
      <NRSelect
        sxProps={{
          width: 'fit-content',
          minWidth: 112,
          maxWidth: 260,
          flexShrink: 0,
          '& .MuiSelect-select': {
            whiteSpace: 'nowrap'
          }
        }}
        size={'small'}
        variant={'outlined'}
        id="activity-type-selector"
        value={selectedActivities}
        showEmptyValue={false}
        onChange={setSelectedActivities}
        options={activityOptions}
        disabled={isActivitySelectDisabled}
        label={'Activity'}
        multiple
      />
      <Tooltip title="Export to CSV">
        <IconButton
          onClick={() => setExportToCsvDialogIsOpen(true)}
          color="secondary"
          size="small"
          sx={{
            padding: 0.75,
            flexShrink: 0
          }}
        >
          <NRExportIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
});

/** Timeline rail: each row draws only its own segment(s). Adjacent rows meet at the shared edge — no need to know the next item's height. */
function getTimelineConnectorSx({ theme, left, avatarCenter, avatarRadius, showIncomingLine, showOutgoingLine }) {
  if (!showIncomingLine && !showOutgoingLine) return {};

  const lineSx = {
    content: '""',
    position: 'absolute',
    left,
    borderLeft: `1px solid ${theme.palette.divider}`,
    zIndex: 0
  };

  if (showIncomingLine && showOutgoingLine) {
    // Full height; avatar background covers the middle
    Object.assign(lineSx, { top: 0, bottom: 0 });
  } else if (showOutgoingLine) {
    Object.assign(lineSx, { top: `calc(${avatarCenter} + ${avatarRadius})`, bottom: 0 });
  } else {
    Object.assign(lineSx, { top: 0, height: `calc(${avatarCenter} - ${avatarRadius})` });
  }

  return { '&::before': lineSx };
}

function ActivityFeedItem({ item, showTime = true, handleClose, showIncomingLine = false, showOutgoingLine = false }) {
  const theme = useTheme();
  const createdAtAsDate = new Date(item.createdAt);
  const avatarSize = theme.spacing(4.5); // matches AltAvatar
  const avatarRadius = `calc(${avatarSize} / 2)`;
  // time column (104) + gap (spacing 2) + half avatar — vertical center of the icon
  const connectorLeft = `calc(104px + ${theme.spacing(2)} + ${avatarRadius})`;
  // With py + alignItems: center, the avatar center is the row midpoint
  const avatarCenter = '50%';

  return (
    <Box
      key={item.id}
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 1.5,
        position: 'relative',
        ...(showTime ? getTimelineConnectorSx({ theme, left: connectorLeft, avatarCenter, avatarRadius, showIncomingLine, showOutgoingLine }) : {})
      }}
    >
      {showTime && <NRRelativeTime sx={{ width: 104, marginRight: 2, textAlign: 'right', flexShrink: 0 }} variant="body2" value={createdAtAsDate} />}
      <AltAvatar aria-hidden="true" sx={{ position: 'relative', zIndex: 1 }}>
        <NRActivityFeedIcon item={item} />
      </AltAvatar>
      <Box
        sx={{
          marginLeft: 2,
          overflow: 'hidden',
          width: '100%',
          minHeight: 44,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center'
        }}
      >
        <NRActivityFeedItemHeader item={item} handleClose={handleClose} />
      </Box>
    </Box>
  );
}

function ActivityFeedGroupItem({ items, handleClose, showIncomingLine = false, showOutgoingLine = false }) {
  const theme = useTheme();
  const createdAtAsDate = new Date(items[0].createdAt);
  const mainItem = items[0];
  const [expanded, setExpanded] = useState(false);
  const handleExpandedChange = useCallback((_event, isExpanded) => {
    setExpanded(isExpanded);
  }, []);
  const avatarSize = theme.spacing(4.5);
  const avatarRadius = `calc(${avatarSize} / 2)`;
  const connectorLeft = `calc(104px + ${theme.spacing(2)} + ${avatarRadius})`;
  // Group avatar is top-aligned: py (1.5) + inner pt (0.5) + radius
  const avatarCenter = `calc(${theme.spacing(2)} + ${avatarRadius})`;
  // When expanded, only keep the incoming segment — details has its own internal line
  const outgoing = showOutgoingLine && !expanded;

  return (
    <Box
      key={mainItem.id}
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        py: 1.5,
        position: 'relative',
        ...getTimelineConnectorSx({
          theme,
          left: connectorLeft,
          avatarCenter,
          avatarRadius,
          showIncomingLine,
          showOutgoingLine: outgoing
        })
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'start', pt: 0.5 }} tabIndex={0}>
        <NRRelativeTime
          sx={{ width: 104, marginRight: 2, marginTop: '8px', textAlign: 'right', flexShrink: 0 }}
          variant="body2"
          value={createdAtAsDate}
        />
        <AltAvatar aria-hidden="true" sx={{ position: 'relative', zIndex: 1 }}>
          <MultipleIcon />
        </AltAvatar>
      </Box>
      <Accordion
        slotProps={{ transition: { unmountOnExit: true } }}
        expanded={expanded}
        onChange={handleExpandedChange}
        sx={{
          minHeight: '36px',
          // 156px is the width of the time column
          width: 'calc(100% - 156px)',
          '&.Mui-expanded': {
            minHeight: '36px',
            marginTop: 0
          },
          '&::before': {
            display: 'none'
          }
        }}
        key={`panel${mainItem.id}-${mainItem.dayLabel}`}
        elevation={0}
      >
        <AccordionSummary
          sx={{
            justifyContent: 'start',
            minHeight: '36px',
            '&.Mui-expanded': {
              minHeight: '36px'
            },
            '.MuiAccordionSummary-content': {
              flexGrow: 'unset',
              margin: '0px'
            }
          }}
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`panel${mainItem.id}-header`}
          id={`panel${mainItem.id}-${mainItem.dayLabel}`}
        >
          <Typography component="span" sx={{ height: 44, display: 'flex', alignItems: 'center' }}>{`${items.length} updates`}</Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            overflow: 'hidden',
            pr: 0,
            // the line to the left of the accordion
            '&::before': {
              content: '""',
              position: 'absolute',
              left: -18,
              top: 40,
              height: 'calc(100% - 28px)',
              borderLeft: `1px solid ${theme.palette.divider}`
            }
          }}
        >
          {!!items?.length && (
            <NRActivityFeedVirtualizedList
              width="auto"
              list={items}
              Component={({ index, style }) => (
                <Box style={style}>
                  <ActivityFeedItem key={items[index].id} item={items[index]} handleClose={handleClose} showTime={false} />
                </Box>
              )}
              listHeight={600}
            />
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

const ActivityFeedItems = React.memo(function ActivityFeedItems({ items, handleClose }) {
  return (
    <Box sx={{}}>
      {items.map(({ dayLabel, timeGroups }, idx) => (
        <Accordion
          slotProps={{ transition: { unmountOnExit: true } }}
          sx={{
            '&::before': {
              display: 'none'
            }
          }}
          key={`panel${idx}-${dayLabel}`}
          elevation={0}
          defaultExpanded={idx === 0}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`panel${idx}-header`} id={`panel${idx}-${dayLabel}`}>
            <Typography variant="subtitle2">{dayLabel}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{}}>
            {timeGroups.map((timeGroup, idx, arr) =>
              timeGroup.length === 1 ? (
                <ActivityFeedItem
                  handleClose={handleClose}
                  key={timeGroup[0].id}
                  item={timeGroup[0]}
                  showIncomingLine={idx > 0}
                  showOutgoingLine={idx < arr.length - 1}
                />
              ) : (
                <ActivityFeedGroupItem
                  handleClose={handleClose}
                  key={`group-${timeGroup[0].id}`}
                  items={timeGroup}
                  showIncomingLine={idx > 0}
                  showOutgoingLine={idx < arr.length - 1}
                />
              )
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
});

const matchesSearch = (input, ...fields) => {
  const search = input.toLowerCase();
  return fields.some(f => f?.toLowerCase().includes(search));
};

function ActivityFeedContent({
  activity,
  isLoading,
  handleClose,
  exportToCsvConfig,
  totalCount,
  pageInfo = {},
  pageSize = DEFAULT_PAGE_SIZE,
  currentPage = 1,
  handleFetchMore,
  handleSetPageSize
}) {
  const [searchInput, setSearchInput] = useState('');
  const [selectedEntity, setSelectedEntity] = useState(ActivityEntityOptions[0].value);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [exportToCsvDialogIsOpen, setExportToCsvDialogIsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [itemsToDisplay, setItemsToDisplay] = useState([]);
  const [filteredCount, setFilteredCount] = useState(0);

  const activityOptions = useMemo(() => ACTIVITY_OPTIONS_BY_ENTITY[selectedEntity] ?? [], [selectedEntity]);

  const hasClientFilters = selectedEntity !== 'ALL' || selectedActivities.length > 0 || !!searchInput;

  const paginationSummary = useMemo(() => {
    const pageItemsCount = activity?.length ?? 0;
    const resolvedTotal = totalCount ?? 0;
    const hasPageItems = pageItemsCount > 0;
    if (!hasPageItems) return null;

    const startItem = pageSize * (Math.max(currentPage, 1) - 1) + 1;
    const endItem = Math.min(startItem + pageItemsCount - 1, resolvedTotal || startItem + pageItemsCount - 1);
    const rangeLabel = resolvedTotal
      ? `${startItem.toLocaleString()}-${endItem.toLocaleString()} of ${resolvedTotal.toLocaleString()}`
      : `${startItem.toLocaleString()}-${endItem.toLocaleString()}`;

    if (hasClientFilters) {
      return (
        <>
          <Box component="span" sx={{ fontWeight: 'bold' }}>
            {filteredCount.toLocaleString()}
          </Box>
          {` matching · ${rangeLabel}`}
        </>
      );
    }
    return rangeLabel;
  }, [activity?.length, totalCount, pageSize, currentPage, hasClientFilters, filteredCount]);

  const enhancedActivity = useMemo(
    () =>
      activity.map(item => ({
        ...item,
        activityDate: getDayLabel(item.createdAt)
      })),
    [activity]
  );

  // Defer heavy computation to avoid blocking UI
  useEffect(() => {
    setIsProcessing(true);

    // Use setTimeout to allow spinner to render
    const timer = setTimeout(() => {
      const lowerCaseSearchInput = searchInput.toLowerCase();

      const filtered = enhancedActivity.filter(item => {
        const isEntityTypeMatch = selectedEntity === ActivityEntityOptions[0].value || item.entityType === selectedEntity;
        if (!isEntityTypeMatch) return false;

        const isActivityTypeMatch = selectedActivities.length === 0 || selectedActivities.includes(item.activityType);
        if (!isActivityTypeMatch) return false;

        if (!searchInput) return true;

        return matchesSearch(
          lowerCaseSearchInput,
          item.activityType,
          item.user,
          item.description,
          item.entity?.cve,
          item.entity?.checkName,
          item.entity?.name,
          item.entity?.componentName,
          item.entity?.userName,
          item.entity?.hash,
          item.entity?.checkId,
          item.entity?.groupId
        );
      });

      setFilteredCount(filtered.length);
      const grouped = groupActivitiesForDisplay(filtered);
      setItemsToDisplay(grouped);
      setIsProcessing(false);
    }, 0);

    return () => clearTimeout(timer);
  }, [enhancedActivity, searchInput, selectedEntity, selectedActivities]);

  const handleSearchChange = useCallback(value => {
    setSearchInput(value);
  }, []);

  const handleEntityChange = useCallback(value => {
    setSelectedEntity(value);
    setSelectedActivities([]);
  }, []);

  const handleActivitiesChange = useCallback(value => {
    setSelectedActivities(value);
  }, []);

  const showLoading = isLoading || isProcessing;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box',
        padding: 2
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <ActivityFeedQuickSearch
          searchInput={searchInput}
          setSearchInput={handleSearchChange}
          selectedEntity={selectedEntity}
          setSelectedEntity={handleEntityChange}
          selectedActivities={selectedActivities}
          setSelectedActivities={handleActivitiesChange}
          activityOptions={activityOptions}
          isLoading={showLoading}
          setExportToCsvDialogIsOpen={setExportToCsvDialogIsOpen}
        />
        {!!handleFetchMore && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 24 }} aria-live="polite">
              {isLoading ? (
                <CircularProgress size={16} color="primary" aria-label="Loading activities" />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {paginationSummary}
                </Typography>
              )}
            </Box>
            <NRCursorPagination
              pageSizes={[100, 500, 1000, 5000]}
              defaultPageSize={pageSize}
              pageInfo={pageInfo}
              handleFetchMore={handleFetchMore}
              handleSetPageSize={handleSetPageSize}
            />
          </Box>
        )}
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <ActivityFeedItems handleClose={handleClose} items={itemsToDisplay} />
      </Box>
      <NRExportToCsvDialog
        isOpen={exportToCsvDialogIsOpen}
        setIsOpen={setExportToCsvDialogIsOpen}
        selectAllFetchConfig={exportToCsvConfig}
        customCSVExportRouteName={'activities'}
        totalCount={totalCount}
      />
    </Box>
  );
}
export default ActivityFeedContent;

export function getDayLabel(dateString) {
  const date = moment(dateString).startOf('day');
  const today = moment().startOf('day');
  const yesterday = moment().subtract(1, 'day').startOf('day');

  if (date.isSame(today, 'day')) {
    return 'Today';
  } else if (date.isSame(yesterday, 'day')) {
    return 'Yesterday';
  }

  const daysAgo = today.diff(date, 'days');

  if (daysAgo >= 2 && daysAgo <= 7) {
    return date.format('dddd'); // e.g., "Monday"
  } else if (date.isSame(today, 'year')) {
    return date.format('MMMM D'); // e.g., "August 10"
  } else {
    return date.format('MMMM D, YYYY'); // e.g., "August 10, 2022"
  }
}

function groupByTimeWindow(activities, thresholdMinutes = 5) {
  const sorted = [...activities].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const { groups, currentGroup } = sorted.reduce(
    (acc, activity) => {
      const { groups, currentGroup } = acc;

      if (currentGroup.length === 0) {
        currentGroup.push(activity);
        return acc;
      }

      const firstInGroup = currentGroup[0];
      const diff = moment(firstInGroup.createdAt).diff(activity.createdAt, 'minutes');

      if (diff <= thresholdMinutes) {
        currentGroup.push(activity);
      } else {
        groups.push([...currentGroup]);
        acc.currentGroup = [activity]; // reset current group
      }

      return acc;
    },
    { groups: [], currentGroup: [] }
  );

  if (currentGroup.length) {
    groups.push(currentGroup);
  }

  return groups;
}

export function groupActivitiesForDisplay(activityList) {
  const groupedByDay = {};

  for (const activity of activityList) {
    const label = getDayLabel(activity.createdAt);
    if (!groupedByDay[label]) groupedByDay[label] = [];
    groupedByDay[label].push(activity);
  }

  // Sort days by date (newest first) before grouping into time clusters
  return Object.entries(groupedByDay)
    .sort(([, activitiesA], [, activitiesB]) => {
      // Sort by the most recent activity in each day group
      return new Date(activitiesB[0].createdAt) - new Date(activitiesA[0].createdAt);
    })
    .map(([dayLabel, activities]) => ({
      dayLabel,
      timeGroups: groupByTimeWindow(activities)
    }));
}
